import { CommonModule } from '@angular/common';
import {
  Component,
  ComponentRef,
  DoCheck,
  EnvironmentInjector,
  inject, OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { BreakPoint, BreakpointService } from '@shared/services/breakpoint.service';
import { Subject, takeUntil } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';
import { NMFTAAlertComponent, NMFTAButton, NMFTAButtonComponent, NMFTAIconComponent } from '@nmfta/nmfta-ui';

export interface DynamicComponent {
  data: unknown;
}

export function openCloseConfirmation(
  dialog: MatDialog,
  message: string | undefined,
  onConfirm: () => void,
): void {
  const confirmRef = dialog.open(DialogComponent, {
    width: '420px',
    data: {
      title: 'Confirm',
      hideIcon: true,
      hideClose: true,
      message,
      actions: [
        {
          id: 'cancel',
          label: 'Cancel',
          styleType: 'outlined',
          onClick: () => confirmRef.close(),
        },
        {
          id: 'confirm',
          label: 'Confirm',
          color: 'accent',
          styleType: 'contained',
          onClick: () => {
            confirmRef.close();
            onConfirm();
          },
        },
      ],
    },
  });
}

@Component({
  selector: 'nmfta-dialog',
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogContent,
    MatDialogActions,
    NMFTAButtonComponent,
    MatIconModule,
    NMFTAIconComponent,
    MatMenu,
    MatMenuTrigger,
    MatMenuItem,
    NMFTAAlertComponent,
    MatTooltip,
  ],
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss']
})
export class DialogComponent implements OnInit, DoCheck, OnDestroy {
  @ViewChild('dynamicContent', { read: ViewContainerRef, static: true })
  dynamicContent!: ViewContainerRef;

  contentComponentRef: ComponentRef<DynamicComponent> | undefined;

  readonly breakpointService = inject(BreakpointService);

  private readonly dialogRef = inject(MatDialogRef<DialogComponent>);
  private readonly confirmDialog = inject(MatDialog);
  readonly data = inject(MAT_DIALOG_DATA);
  /* eslint-disable @typescript-eslint/no-explicit-any */
  private _previousData: any;

  leftActions: NMFTAButton[] = [];
  rightActions: NMFTAButton[] = [];

  isDesktop = false;
  private destroy$ = new Subject<void>();

  readonly environmentInjector = inject(EnvironmentInjector);

  ngOnInit(): void {
    this.processActions();
    this.renderContent();

    this.isDesktop = this.breakpointService.isBreakpointOrLarger(BreakPoint.LG);

    // Observe if screen is desktop size
    this.breakpointService
      .observeIsBreakpointOrLarger(BreakPoint.LG)
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDesktop) => {
        this.isDesktop = isDesktop;
      });
  }

  ngDoCheck() {
    if (this._previousData !== this.data) {
      this._previousData = this.data;
      this.processActions();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Renders either a TemplateRef or a dynamic component into the dialog.
   *
   * This function checks if `data.content` is defined before attempting to render it.
   * Early return because not all dialogs require dynamic content — some only display a static message.
   *
   * When rendering a dynamic component, we check for Angular's internal `ɵcmp` marker to ensure
   * the content is a valid Component type. While this uses a non-ASCII/private property, it is currently
   * the safest way to detect a component type at runtime.
   *
   * The `environmentInjector` is required when rendering standalone components.
   * It provides the necessary DI context so that the dynamically created component can resolve its dependencies.
   */
  private renderContent(): void {
    if (!this.data.content) return;

    if (this.data.content instanceof TemplateRef) {
      this.dynamicContent.createEmbeddedView(this.data.content);
    }
    else if ((this.data.content as any)?.ɵcmp) {
      this.contentComponentRef = this.dynamicContent.createComponent(this.data.content, {
        environmentInjector: this.environmentInjector,
      });

      if (this.data.dataForComponent) {
        this.contentComponentRef.instance.data = this.data.dataForComponent;
      }
    }
  }

  onClose(): void {
    const shouldConfirm = typeof this.data.confirmBeforeClose === 'function'
      ? this.data.confirmBeforeClose()
      : this.data.confirmBeforeClose;

    if (shouldConfirm) {
      openCloseConfirmation(
        this.confirmDialog,
        this.data.confirmBeforeCloseMessage,
        () => this.dialogRef.close({ closedWithoutSave: true }),
      );
    } else {
      this.dialogRef.close({ closedWithoutSave: true });
    }
  }

  private processActions() {
    // Reset arrays before processing
    this.leftActions = [];
    this.rightActions = [];

    if (Array.isArray(this.data.actions)) {
      for (const action of this.data.actions) {
        if (action.position === 'left') {
          this.leftActions.push(action);
        } else {
          this.rightActions.push(action);
        }
      }
    }
  }
}
